import { Alert, Box, Stack, Typography } from '@mui/material';
import { useCallback, useId, useMemo, useState, type ReactElement } from 'react';
import { useSearchParams } from 'react-router';
import { useHttpClient } from '@/app/providers';
import { CONFLICTS_PATH, accountConflictsPath } from '@/admin/conflicts/conflictRoutes';
import { useAdminQuery, useRefreshOnReturn } from '@/admin/data/useAdminQuery';
import { useAdminAccess } from '@/admin/users/useAdminAccess';
import { messageOf } from '@/admin/users/adminErrors';
import { adminErrorCode } from '@/admin/settings/adminErrorMessage';
import { SensitiveActionDialog } from '@/admin/SensitiveActionDialog';
import { SENSITIVE_ACTIONS } from '@/admin/sensitiveActions';
import type { SensitiveActionInput } from '@/admin/adminApi';
import {
  AccessDenied,
  Badge,
  Button,
  Card,
  DataTable,
  ErrorState,
  Field,
  LoadingState,
  PageHeader,
  SelectField,
  type DataTableColumn,
} from '@/ui/kit';
import { createSyncSupervisionApi, type SyncAttempt, type SyncRecoveryRequest } from './syncSupervisionApi';
import {
  ATTEMPT_LABELS,
  ATTEMPT_TONES,
  REQUEST_LABELS,
  causeOf,
  instant,
  pagedCountLabel,
  resultLabel,
} from './syncPresentation';

interface RecoveryDialog {
  readonly attempt: SyncAttempt;
  readonly action: 'retry' | 'resync';
  readonly key: string;
}
const policy = SENSITIVE_ACTIONS.recover_synchronization;
const pageOf = (value: string | null): number =>
  /^\d+$/u.test(value ?? '') && Number(value) > 0 && Number(value) <= 100000 ? Number(value) : 1;

export const SyncSupervisionScreen = (): ReactElement => {
  const http = useHttpClient();
  const api = useMemo(() => createSyncSupervisionApi(http), [http]);
  const { access, reload: reloadAccess } = useAdminAccess();
  const [params, setParams] = useSearchParams();
  const page = pageOf(params.get('page'));
  const requestPage = pageOf(params.get('requestPage'));
  const status = params.get('status') ?? '';
  const userId = params.get('userId') ?? '';
  const [userDraft, setUserDraft] = useState(userId);
  const [token, setToken] = useState(0);
  const refresh = useCallback(() => {
    setToken((value) => value + 1);
  }, []);
  useRefreshOnReturn(refresh);
  const enabled = access.kind === 'granted';
  const attempts = useAdminQuery(
    `sync:${String(page)}:${status}:${userId}`,
    () => api.attempts(page, status, userId),
    { enabled, token },
  );
  const requests = useAdminQuery(
    `sync-requests:${String(requestPage)}:${userId}`,
    () => api.requests(requestPage, userId),
    { enabled, token },
  );
  const [selected, setSelected] = useState<SyncAttempt | null>(null);
  const [dialog, setDialog] = useState<RecoveryDialog | null>(null);
  const abandonedNoteId = useId();
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const admin = access.kind === 'granted' && access.role === 'admin';
  const setParam = (name: string, value: string): void => {
    const next = new URLSearchParams(params);
    if (value === '') next.delete(name);
    else next.set(name, value);
    setParams(next);
  };
  const openRecovery = (action: 'retry' | 'resync'): void => {
    if (selected === null) return;
    setError(null);
    setErrorCode(null);
    setDialog({ attempt: selected, action, key: crypto.randomUUID() });
  };
  const confirm = async (input: SensitiveActionInput): Promise<void> => {
    if (dialog === null || pending) return;
    setPending(true);
    try {
      await api.recover(dialog.attempt.id, dialog.action, dialog.key, input);
      setDialog(null);
      setNotice(
        'Demande enregistrée. L’appareil l’exécutera à son prochain contact ; aucune résolution n’est encore confirmée.',
      );
      refresh();
    } catch (failure: unknown) {
      const code = adminErrorCode(failure);
      setErrorCode(code);
      setError(
        code === 'recovery_already_pending'
          ? 'Une demande est déjà active pour cet appareil. Actualisez son historique.'
          : code === 'device_unavailable'
            ? 'Cet appareil n’a plus de session active. La personne doit se reconnecter.'
            : messageOf(failure),
      );
    } finally {
      setPending(false);
    }
  };

  const attemptColumns: readonly DataTableColumn<SyncAttempt>[] = [
    { key: 'account', header: 'Compte', primary: true, render: (item) => item.email ?? 'Compte actif' },
    {
      key: 'state',
      header: 'Statut de la tentative',
      render: (item) => <Badge label={ATTEMPT_LABELS[item.status]} tone={ATTEMPT_TONES[item.status]} />,
    },
    { key: 'cause', header: 'Cause', render: (item) => causeOf(item.reason)[0] },
    {
      key: 'direction',
      header: 'Échange',
      render: (item) => (item.direction === 'push' ? 'Envoi' : 'Réception'),
    },
    { key: 'time', header: 'Date serveur', render: (item) => instant(item.occurredAt) },
  ];
  const requestColumns: readonly DataTableColumn<SyncRecoveryRequest>[] = [
    {
      key: 'account',
      header: 'Compte (identifiant)',
      render: (item) => (
        <Box component="span" sx={{ overflowWrap: 'anywhere' }}>
          {item.userId}
        </Box>
      ),
    },
    {
      key: 'action',
      header: 'Action demandée',
      primary: true,
      render: (item) => (item.action === 'resync' ? 'Relecture complète' : 'Relance technique'),
    },
    {
      key: 'state',
      header: 'État',
      render: (item) => (
        <Badge
          label={REQUEST_LABELS[item.status]}
          tone={
            item.status === 'completed' && item.resultCode === 'cycle_finished'
              ? 'success'
              : item.status === 'failed'
                ? 'danger'
                : 'warning'
          }
        />
      ),
    },
    {
      key: 'device',
      header: 'Appareil',
      render: (item) => (
        <Box component="span" sx={{ overflowWrap: 'anywhere' }}>
          {item.deviceHash}
        </Box>
      ),
    },
    { key: 'date', header: 'Demandée le', render: (item) => instant(item.createdAt) },
    { key: 'result', header: 'Résultat', render: (item) => resultLabel(item.resultCode) },
  ];

  return (
    <Stack spacing={3}>
      <PageHeader
        eyebrow="Exploitation"
        title="Synchronisations"
        subtitle="Comprendre les erreurs et suivre les récupérations, sans modifier les saisies des familles."
        actions={
          <>
            <Button to={CONFLICTS_PATH}>Voir les conflits</Button>
            <Button onClick={refresh}>Actualiser</Button>
          </>
        }
      />
      <Alert severity="info" role="note">
        Vue du serveur depuis l’installation de cette supervision. Les erreurs purement hors ligne ne sont pas
        visibles ici. Une ancienne erreur ne prouve pas qu’un conflit reste non résolu : le registre des conflits dit
        lesquels restent ouverts.
      </Alert>
      {access.kind === 'loading' ? <LoadingState label="Vérification de l’accès" /> : null}
      {access.kind === 'denied' || attempts.failure?.forbidden || requests.failure?.forbidden ? (
        <AccessDenied />
      ) : null}
      {access.kind === 'error' ? <ErrorState message={access.message} onRetry={reloadAccess} /> : null}
      {enabled ? (
        <>
          {notice === null ? null : (
            <Alert severity="info" role="status">
              {notice}
            </Alert>
          )}
          <Card title="Historique des tentatives">
            <Box
              component="form"
              onSubmit={(event) => {
                event.preventDefault();
                const next = new URLSearchParams(params);
                next.delete('page');
                next.delete('requestPage');
                if (userDraft.trim() === '') next.delete('userId');
                else next.set('userId', userDraft.trim());
                setSelected(null);
                setParams(next);
              }}
              sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', gap: 2, mb: 2 }}
            >
              <SelectField
                label="Statut"
                value={status === '' ? 'all' : status}
                options={[
                  { value: 'all', label: 'Tous les statuts' },
                  ...Object.entries(ATTEMPT_LABELS).map(([value, label]) => ({ value, label })),
                ]}
                onChange={(value) => {
                  const next = new URLSearchParams(params);
                  next.delete('page');
                  next.set('status', value === 'all' ? '' : value);
                  setSelected(null);
                  setParams(next);
                }}
              />
              <Field label="Identifiant du compte" value={userDraft} onChange={setUserDraft} />
              <Button type="submit">Filtrer</Button>
            </Box>
            {attempts.busy ? <LoadingState label="Lecture des tentatives" /> : null}
            {attempts.failure === null ? null : (
              <ErrorState message={attempts.failure.message} onRetry={refresh} />
            )}
            {attempts.data === null ? null : (
              <>
                <DataTable
                  label="Tentatives de synchronisation"
                  rows={attempts.data.items}
                  columns={attemptColumns}
                  getRowId={(item) => item.id}
                  onRowSelect={setSelected}
                  selectedRowId={selected?.id}
                  emptyMessage="Aucune tentative pour ces filtres."
                />
                <Stack direction="row" spacing={2} sx={{ mt: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                  <Button
                    disabled={page <= 1}
                    onClick={() => {
                      setParam('page', String(page - 1));
                    }}
                  >
                    Précédent
                  </Button>
                  <Typography>
                    {pagedCountLabel(attempts.data.total, 'tentative', 'tentatives', page)}
                  </Typography>
                  <Button
                    disabled={page * 25 >= attempts.data.total}
                    onClick={() => {
                      setParam('page', String(page + 1));
                    }}
                  >
                    Suivant
                  </Button>
                </Stack>
              </>
            )}
          </Card>
          {selected === null ? null : (
            <Card title={causeOf(selected.reason)[0]} subtitle={selected.email ?? 'Compte actif'}>
              <Stack spacing={2}>
                <Typography>{causeOf(selected.reason)[1]}</Typography>
                <Typography sx={{ overflowWrap: 'anywhere' }}>
                  Tentative : {selected.id} · Compte : {selected.userId} · Appareil :{' '}
                  {selected.deviceHash ?? 'Non identifié'}
                </Typography>
                {selected.status === 'conflict' ? (
                  <Button to={accountConflictsPath(selected.userId)}>Voir les conflits de ce compte</Button>
                ) : null}
                <Button
                  onClick={() => {
                    setUserDraft(selected.userId);
                    const next = new URLSearchParams(params);
                    next.set('userId', selected.userId);
                    next.delete('page');
                    next.delete('requestPage');
                    setParams(next);
                  }}
                >
                  Voir l’historique de ce compte
                </Button>
                {!admin ? (
                  <Alert severity="info">Votre rôle permet la consultation uniquement.</Alert>
                ) : selected.deviceHash === null ? (
                  <Alert severity="warning">
                    Aucun appareil identifié : la personne doit se reconnecter avant une récupération ciblée.
                  </Alert>
                ) : (
                  <Stack spacing={1}>
                    <Stack direction="row" spacing={2} useFlexGap sx={{ flexWrap: 'wrap' }}>
                      <Button
                        aria-describedby={abandonedNoteId}
                        onClick={() => {
                          openRecovery('retry');
                        }}
                      >
                        Demander une relance
                      </Button>
                      <Button
                        variant="secondary"
                        onClick={() => {
                          openRecovery('resync');
                        }}
                      >
                        Demander une resynchronisation complète
                      </Button>
                    </Stack>
                    {/* The device enforces it (ADR 0038): the request targets a device, not an entry. */}
                    <Typography id={abandonedNoteId}>
                      Les saisies abandonnées par le parent ne sont jamais relancées.
                    </Typography>
                  </Stack>
                )}
              </Stack>
            </Card>
          )}
          <Card
            title="Historique des actions correctives"
            subtitle="Demandes ciblées, valables 24 h. Un ancien mobile ne les exécute pas ; il doit être mis à jour."
          >
            {requests.busy ? <LoadingState label="Lecture des récupérations" /> : null}
            {requests.failure === null ? null : (
              <ErrorState message={requests.failure.message} onRetry={refresh} />
            )}
            {requests.data === null ? null : (
              <>
                <DataTable
                  label="Actions correctives"
                  rows={requests.data.items}
                  columns={requestColumns}
                  getRowId={(item) => item.id}
                  emptyMessage="Aucune récupération demandée."
                />
                <Stack direction="row" spacing={2} sx={{ mt: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                  <Button
                    disabled={requestPage <= 1}
                    onClick={() => {
                      setParam('requestPage', String(requestPage - 1));
                    }}
                  >
                    Actions précédentes
                  </Button>
                  <Typography>
                    {pagedCountLabel(requests.data.total, 'demande', 'demandes', requestPage)}
                  </Typography>
                  <Button
                    disabled={requestPage * 25 >= requests.data.total}
                    onClick={() => {
                      setParam('requestPage', String(requestPage + 1));
                    }}
                  >
                    Actions suivantes
                  </Button>
                </Stack>
              </>
            )}
          </Card>
        </>
      ) : null}
      {dialog === null || access.kind !== 'granted' ? null : (
        <SensitiveActionDialog
          open
          title={
            dialog.action === 'retry' ? 'Demander une relance' : 'Demander une resynchronisation complète'
          }
          subtitle={`${dialog.attempt.email ?? 'Compte actif'} · ${dialog.attempt.deviceHash ?? ''}`}
          consequence={{
            summary:
              'Exécution au prochain contact de cet appareil, pas immédiatement. Aucun conflit métier n’est arbitré.',
            disappearsTitle: 'Aucune suppression',
            disappears: ['Aucune saisie ni aucun mouvement de stock.'],
            kept: [
              'Les données SQLite et toutes les opérations de l’outbox.',
              'Les clés d’idempotence, les permissions et les conflits à arbitrer.',
              'La demande et son résultat dans l’historique.',
            ],
          }}
          reasonOptions={policy.reasons}
          requiresPassword={policy.requiresPassword}
          operatorEmail={access.identity.email}
          expectedConfirmation={dialog.attempt.id}
          confirmationLabel="Identifiant de la tentative"
          actionLabel="Enregistrer la demande"
          pending={pending}
          error={error}
          errorCode={errorCode}
          onCancel={() => {
            if (!pending) setDialog(null);
          }}
          onConfirm={(input) => {
            void confirm(input);
          }}
        />
      )}
    </Stack>
  );
};
